'use client';

import React, { useRef, useEffect, useState, useCallback, useSyncExternalStore } from 'react';

/* ── Stock-themed loading tips ─────────────────────────────────────── */
const LOADING_TIPS = [
  'Analyzing financial statements...',
  'Scanning macro-economic indicators...',
  'Evaluating analyst consensus...',
  'Crunching key metrics...',
  'Assessing risk factors...',
  'Reviewing earnings history...',
  'Mapping technical signals...',
  'Identifying growth catalysts...',
  'Cross-referencing sector trends...',
  'Compiling executive summary...',
];

/* ── Types ─────────────────────────────────────────────────────────── */
interface Candle {
  x: number;
  width: number;
  gapHeight: number; // gap in the middle where the bull can pass
  gapY: number;
  scored: boolean;
  color: 'red' | 'green';
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
}

/* ── Constants ─────────────────────────────────────────────────────── */
const CANVAS_W = 480;
const CANVAS_H = 320;
const BULL_SIZE = 22;
const GRAVITY = 0.55;
const JUMP_FORCE = -9;
const CANDLE_SPEED_BASE = 2.5;
const CANDLE_GAP = 110;
const CANDLE_WIDTH = 32;
const CANDLE_SPAWN_INTERVAL = 110; // frames

const GREEN = '#22c55e';
const RED = '#ef4444';
const GOLD = '#eab308';
const ACCENT_BLUE = '#3b82f6';
const BG_DARK = '#0f172a';
const SURFACE = '#1e293b';
const TEXT_DIM = '#64748b';
const TEXT_LIGHT = '#f8fafc';

interface ResearchLoadingGameProps {
  ready?: boolean;
  limitHit?: boolean;
  onDismiss?: () => void;
}

export default function ResearchLoadingGame({ ready = false, limitHit = false, onDismiss }: ResearchLoadingGameProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef(0);
  const readyRef = useRef(false);
  const limitHitRef = useRef(false);
  const gameRef = useRef({
    bull: { y: CANVAS_H / 2, vy: 0 },
    candles: [] as Candle[],
    particles: [] as Particle[],
    score: 0,
    highScore: 0,
    gameOver: false,
    started: false,
    frameCount: 0,
    speed: CANDLE_SPEED_BASE,
    chartPoints: [] as number[],
  });
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [started, setStarted] = useState(false);
  const [tipIndex, setTipIndex] = useState(0);
  const prefersReducedMotion = useSyncExternalStore(
    (cb) => {
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      mq.addEventListener('change', cb);
      return () => mq.removeEventListener('change', cb);
    },
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    () => false,
  );

  // Keep refs in sync with props
  useEffect(() => {
    readyRef.current = ready;
  }, [ready]);
  useEffect(() => {
    limitHitRef.current = limitHit;
  }, [limitHit]);

  // Cycle loading tips
  useEffect(() => {
    const interval = setInterval(() => {
      setTipIndex((i) => (i + 1) % LOADING_TIPS.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  /* ── Spawn candle ─────────────────────────────────────────────── */
  const spawnCandle = useCallback(() => {
    const gapHeight = CANDLE_GAP - Math.min(gameRef.current.score * 0.3, 30);
    const minGapY = 50;
    const maxGapY = CANVAS_H - 50 - gapHeight;
    const gapY = minGapY + Math.random() * (maxGapY - minGapY);
    const color = Math.random() > 0.5 ? 'red' : 'green';
    gameRef.current.candles.push({
      x: CANVAS_W + 20,
      width: CANDLE_WIDTH,
      gapHeight,
      gapY,
      scored: false,
      color,
    });
  }, []);

  /* ── Spawn particles ──────────────────────────────────────────── */
  const spawnParticles = useCallback((x: number, y: number, color: string, count: number) => {
    for (let i = 0; i < count; i++) {
      gameRef.current.particles.push({
        x,
        y,
        vx: (Math.random() - 0.5) * 4,
        vy: (Math.random() - 0.5) * 4,
        life: 1,
        maxLife: 20 + Math.random() * 20,
        size: 2 + Math.random() * 3,
        color,
      });
    }
  }, []);

  /* ── Draw bull (triangle/arrow shape) ─────────────────────────── */
  const drawBull = useCallback((ctx: CanvasRenderingContext2D, y: number, vy: number) => {
    const x = 70;
    const angle = Math.atan2(vy, 8) * 0.5; // tilt based on velocity

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);

    // Body glow
    ctx.shadowColor = GREEN;
    ctx.shadowBlur = 12;

    // Main body (diamond/arrow)
    ctx.beginPath();
    ctx.moveTo(BULL_SIZE, 0); // nose
    ctx.lineTo(-BULL_SIZE * 0.4, -BULL_SIZE * 0.6); // top
    ctx.lineTo(-BULL_SIZE * 0.7, 0); // tail
    ctx.lineTo(-BULL_SIZE * 0.4, BULL_SIZE * 0.6); // bottom
    ctx.closePath();
    ctx.fillStyle = GREEN;
    ctx.fill();

    // Inner highlight
    ctx.beginPath();
    ctx.moveTo(BULL_SIZE * 0.6, 0);
    ctx.lineTo(-BULL_SIZE * 0.1, -BULL_SIZE * 0.3);
    ctx.lineTo(-BULL_SIZE * 0.3, 0);
    ctx.lineTo(-BULL_SIZE * 0.1, BULL_SIZE * 0.3);
    ctx.closePath();
    ctx.fillStyle = '#4ade80';
    ctx.fill();

    // Eye
    ctx.shadowBlur = 0;
    ctx.beginPath();
    ctx.arc(BULL_SIZE * 0.2, -BULL_SIZE * 0.15, 2.5, 0, Math.PI * 2);
    ctx.fillStyle = TEXT_LIGHT;
    ctx.fill();

    ctx.restore();

    // Trail particles
    if (gameRef.current.started && !gameRef.current.gameOver) {
      if (gameRef.current.frameCount % 3 === 0) {
        spawnParticles(x - BULL_SIZE * 0.7, y, 'rgba(34,197,94,0.4)', 1);
      }
    }
  }, [spawnParticles]);

  /* ── Draw candle ──────────────────────────────────────────────── */
  const drawCandle = useCallback((ctx: CanvasRenderingContext2D, candle: Candle) => {
    const { x, width, gapHeight, gapY, color } = candle;
    const candleColor = color === 'red' ? RED : GREEN;
    const wickWidth = 3;

    // Top candle body
    ctx.fillStyle = candleColor;
    ctx.shadowColor = candleColor;
    ctx.shadowBlur = 6;

    // Top section
    const topHeight = gapY;
    if (topHeight > 0) {
      // Wick
      ctx.fillRect(x + width / 2 - wickWidth / 2, 0, wickWidth, topHeight);
      // Body
      const bodyTop = Math.max(0, topHeight - 40);
      ctx.fillRect(x, bodyTop, width, topHeight - bodyTop);
      // Body highlight
      ctx.fillStyle = color === 'red' ? '#fca5a5' : '#86efac';
      ctx.globalAlpha = 0.3;
      ctx.fillRect(x + 3, bodyTop + 3, width * 0.4, topHeight - bodyTop - 6);
      ctx.globalAlpha = 1;
    }

    // Bottom section
    ctx.fillStyle = candleColor;
    ctx.shadowColor = candleColor;
    ctx.shadowBlur = 6;
    const bottomY = gapY + gapHeight;
    const bottomHeight = CANVAS_H - bottomY;
    if (bottomHeight > 0) {
      // Wick
      ctx.fillRect(x + width / 2 - wickWidth / 2, bottomY, wickWidth, bottomHeight);
      // Body
      const bodyHeight = Math.min(40, bottomHeight);
      ctx.fillRect(x, bottomY, width, bodyHeight);
      // Body highlight
      ctx.fillStyle = color === 'red' ? '#fca5a5' : '#86efac';
      ctx.globalAlpha = 0.3;
      ctx.fillRect(x + 3, bottomY + 3, width * 0.4, bodyHeight - 6);
      ctx.globalAlpha = 1;
    }

    ctx.shadowBlur = 0;
  }, []);

  /* ── Reset game ───────────────────────────────────────────────── */
  const resetGame = useCallback(() => {
    const g = gameRef.current;
    if (g.score > g.highScore) g.highScore = g.score;
    g.bull = { y: CANVAS_H / 2, vy: 0 };
    g.candles = [];
    g.particles = [];
    g.score = 0;
    g.gameOver = false;
    g.started = true;
    g.frameCount = 0;
    g.speed = CANDLE_SPEED_BASE;
    setScore(0);
    setGameOver(false);
    setStarted(true);
  }, []);

  /* ── Jump / interact ──────────────────────────────────────────── */
  const handleInteract = useCallback(() => {
    const g = gameRef.current;
    if (g.gameOver) {
      resetGame();
      return;
    }
    if (!g.started) {
      g.started = true;
      setStarted(true);
      return;
    }
    g.bull.vy = JUMP_FORCE;
  }, [resetGame]);

  /* ── Main game loop ───────────────────────────────────────────── */
  useEffect(() => {
    if (prefersReducedMotion) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Init background chart line
    const g = gameRef.current;
    if (g.chartPoints.length === 0) {
      let val = CANVAS_H * 0.7;
      for (let i = 0; i < CANVAS_W; i += 3) {
        val += (Math.random() - 0.48) * 4;
        val = Math.max(CANVAS_H * 0.5, Math.min(CANVAS_H * 0.9, val));
        g.chartPoints.push(val);
      }
    }

    const loop = () => {
      g.frameCount++;

      // Clear
      ctx.fillStyle = BG_DARK;
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      // Background grid
      ctx.strokeStyle = 'rgba(51,65,85,0.3)';
      ctx.lineWidth = 0.5;
      for (let x = 0; x < CANVAS_W; x += 40) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, CANVAS_H);
        ctx.stroke();
      }
      for (let y = 0; y < CANVAS_H; y += 40) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(CANVAS_W, y);
        ctx.stroke();
      }

      // Background chart line (scrolling)
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(59,130,246,0.15)';
      ctx.lineWidth = 1.5;
      const offset = g.frameCount % 3;
      for (let i = 0; i < g.chartPoints.length; i++) {
        const x = i * 3 - offset;
        if (i === 0) ctx.moveTo(x, g.chartPoints[i]);
        else ctx.lineTo(x, g.chartPoints[i]);
      }
      ctx.stroke();

      // Shift chart points for scrolling effect
      if (g.frameCount % 6 === 0 && g.started && !g.gameOver) {
        g.chartPoints.shift();
        const last = g.chartPoints[g.chartPoints.length - 1];
        g.chartPoints.push(last + (Math.random() - 0.48) * 6);
      }

      if (g.started && !g.gameOver) {
        // Physics
        g.bull.vy += GRAVITY;
        g.bull.y += g.bull.vy;

        // Boundaries
        if (g.bull.y < BULL_SIZE) {
          g.bull.y = BULL_SIZE;
          g.bull.vy = 0;
        }
        if (g.bull.y > CANVAS_H - BULL_SIZE) {
          g.bull.y = CANVAS_H - BULL_SIZE;
          g.bull.vy = 0;
        }

        // Speed increases over time
        g.speed = CANDLE_SPEED_BASE + g.score * 0.05;

        // Spawn candles
        if (g.frameCount % CANDLE_SPAWN_INTERVAL === 0) {
          spawnCandle();
        }

        // Update candles
        for (let i = g.candles.length - 1; i >= 0; i--) {
          const c = g.candles[i];
          c.x -= g.speed;

          // Score check
          if (!c.scored && c.x + c.width < 70) {
            c.scored = true;
            g.score++;
            setScore(g.score);
            spawnParticles(70, g.bull.y, GOLD, 8);
          }

          // Collision detection
          const bullX = 70;
          const bullR = BULL_SIZE * 0.5;
          if (
            bullX + bullR > c.x &&
            bullX - bullR < c.x + c.width
          ) {
            // Check if bull is in the gap
            if (g.bull.y - bullR < c.gapY || g.bull.y + bullR > c.gapY + c.gapHeight) {
              // Collision!
              g.gameOver = true;
              setGameOver(true);
              spawnParticles(bullX, g.bull.y, RED, 20);
            }
          }

          // Remove off-screen
          if (c.x + c.width < -20) {
            g.candles.splice(i, 1);
          }
        }
      }

      // Draw candles
      for (const c of g.candles) {
        drawCandle(ctx, c);
      }

      // Draw particles
      for (let i = g.particles.length - 1; i >= 0; i--) {
        const p = g.particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.life++;
        const alpha = 1 - p.life / p.maxLife;
        if (alpha <= 0) {
          g.particles.splice(i, 1);
          continue;
        }
        ctx.globalAlpha = alpha;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * alpha, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      // Draw bull
      drawBull(ctx, g.bull.y, g.bull.vy);

      // HUD - Score
      if (g.started) {
        ctx.fillStyle = TEXT_LIGHT;
        ctx.font = 'bold 14px ui-monospace, monospace';
        ctx.textAlign = 'right';
        ctx.fillText(`$${(g.score * 1247.83).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, CANVAS_W - 16, 28);
        ctx.fillStyle = TEXT_DIM;
        ctx.font = '10px ui-monospace, monospace';
        ctx.fillText('PORTFOLIO VALUE', CANVAS_W - 16, 42);
      }

      // High score
      if (g.highScore > 0) {
        ctx.fillStyle = GOLD;
        ctx.font = '10px ui-monospace, monospace';
        ctx.textAlign = 'left';
        ctx.fillText(`BEST: $${(g.highScore * 1247.83).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 16, 28);
      }

      // Start screen
      if (!g.started) {
        ctx.fillStyle = 'rgba(15,23,42,0.6)';
        ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

        ctx.fillStyle = GREEN;
        ctx.font = 'bold 20px ui-sans-serif, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('BULL RUN', CANVAS_W / 2, CANVAS_H / 2 - 30);

        ctx.fillStyle = TEXT_DIM;
        ctx.font = '13px ui-sans-serif, system-ui, sans-serif';
        ctx.fillText('Navigate through the candlesticks', CANVAS_W / 2, CANVAS_H / 2);

        ctx.fillStyle = ACCENT_BLUE;
        ctx.font = '12px ui-sans-serif, system-ui, sans-serif';
        ctx.fillText('Tap or press Space to start', CANVAS_W / 2, CANVAS_H / 2 + 28);
      }

      // Game over overlay
      if (g.gameOver) {
        ctx.fillStyle = 'rgba(15,23,42,0.7)';
        ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

        ctx.fillStyle = RED;
        ctx.font = 'bold 20px ui-sans-serif, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('MARGIN CALL', CANVAS_W / 2, CANVAS_H / 2 - 30);

        ctx.fillStyle = TEXT_LIGHT;
        ctx.font = '14px ui-sans-serif, system-ui, sans-serif';
        ctx.fillText(
          `Portfolio: $${(g.score * 1247.83).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
          CANVAS_W / 2,
          CANVAS_H / 2
        );

        ctx.fillStyle = TEXT_DIM;
        ctx.font = '12px ui-sans-serif, system-ui, sans-serif';
        ctx.fillText('Tap or press Space to retry', CANVAS_W / 2, CANVAS_H / 2 + 28);
      }

      // Subtle bottom-right badge for report ready or limit hit
      if (readyRef.current || limitHitRef.current) {
        const isLimit = limitHitRef.current;
        const badgeText = isLimit ? 'Limit Reached' : 'Report Ready';
        const badgeBg = isLimit ? '#7f1d1d' : '#166534';
        const badgeBorder = isLimit ? RED : GREEN;
        const badgeDot = isLimit ? RED : GREEN;
        const badgeTextColor = isLimit ? '#fecaca' : '#bbf7d0';

        ctx.font = '11px ui-sans-serif, system-ui, sans-serif';
        const textWidth = ctx.measureText(badgeText).width;
        const badgeW = textWidth + 20;
        const badgeH = 24;
        const badgeX = CANVAS_W - badgeW - 12;
        const badgeY = CANVAS_H - badgeH - 12;
        const pulseAlpha = 0.7 + Math.sin(g.frameCount * 0.05) * 0.3;

        // Badge background
        ctx.globalAlpha = pulseAlpha;
        ctx.fillStyle = badgeBg;
        ctx.beginPath();
        ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 6);
        ctx.fill();

        // Badge border
        ctx.strokeStyle = badgeBorder;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 6);
        ctx.stroke();

        // Badge dot
        ctx.fillStyle = badgeDot;
        ctx.beginPath();
        ctx.arc(badgeX + 10, badgeY + badgeH / 2, 3, 0, Math.PI * 2);
        ctx.fill();

        // Badge text
        ctx.fillStyle = badgeTextColor;
        ctx.textAlign = 'left';
        ctx.fillText(badgeText, badgeX + 18, badgeY + badgeH / 2 + 4);
        ctx.globalAlpha = 1;
      }

      frameRef.current = requestAnimationFrame(loop);
    };

    frameRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frameRef.current);
  }, [prefersReducedMotion, spawnCandle, spawnParticles, drawBull, drawCandle]);

  // Keyboard controls
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') {
        e.preventDefault();
        handleInteract();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleInteract]);

  /* ── Reduced motion fallback ──────────────────────────────────── */
  if (prefersReducedMotion) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        {limitHit ? (
          <div
            className="px-5 py-2.5 rounded-xl text-sm font-medium opacity-70"
            style={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)' }}
          >
            Monthly research limit reached
          </div>
        ) : ready ? (
          <button
            onClick={() => onDismiss?.()}
            className="px-5 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-colors"
            style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
          >
            View Research Report
          </button>
        ) : (
          <>
            <div className="relative w-10 h-10">
              <div
                className="absolute inset-0 rounded-full border-2 border-t-transparent animate-spin"
                style={{ borderColor: 'var(--color-accent)', borderTopColor: 'transparent' }}
              />
            </div>
            <p className="text-sm text-[var(--color-text-secondary)] transition-opacity duration-500">
              {LOADING_TIPS[tipIndex]}
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex flex-col items-center gap-3">
      {/* Game canvas */}
      <div
        className="relative rounded-2xl overflow-hidden cursor-pointer select-none"
        style={{
          backgroundColor: BG_DARK,
          border: '1px solid rgba(51,65,85,0.5)',
          maxWidth: '100%',
        }}
        onClick={handleInteract}
        onTouchStart={(e) => {
          e.preventDefault();
          handleInteract();
        }}
        role="button"
        tabIndex={0}
        aria-label="Bull Run mini-game. Press space or tap to jump."
      >
        <canvas
          ref={canvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          className="block w-full"
          style={{ maxWidth: CANVAS_W, imageRendering: 'crisp-edges' }}
        />
      </div>

      {/* Loading tip, View Report button, or Limit hit message */}
      {limitHit ? (
        <div
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium opacity-70"
          style={{
            backgroundColor: 'rgba(239,68,68,0.15)',
            color: '#f87171',
            border: '1px solid rgba(239,68,68,0.3)',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          Monthly research limit reached
        </div>
      ) : ready ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDismiss?.();
          }}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
          style={{
            backgroundColor: 'var(--color-accent)',
            color: '#fff',
            boxShadow: '0 0 20px rgba(34,197,94,0.15)',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
          View Research Report
        </button>
      ) : (
        <div className="flex items-center gap-2 px-4">
          <div
            className="w-1.5 h-1.5 rounded-full animate-pulse"
            style={{ backgroundColor: 'var(--color-accent)' }}
          />
          <p
            className="text-sm text-[var(--color-text-secondary)] transition-opacity duration-500"
            key={tipIndex}
          >
            {LOADING_TIPS[tipIndex]}
          </p>
        </div>
      )}

      {/* Controls hint */}
      <p className="text-xs text-[var(--color-text-muted)]">
        {!started
          ? 'Tap or press Space to play while you wait'
          : gameOver
            ? 'Tap or Space to try again'
            : 'Tap or Space to fly through candle gaps'}
      </p>
    </div>
  );
}
